import { View, Text, Image, StyleSheet } from "@react-pdf/renderer";
import stationFront from "@/components/city-review/assets/station-front.png";
import stationSide from "@/components/city-review/assets/station-side.png";
import { palette } from "./styles";

/**
 * Optional override props in case the parent prefers to pass pre-fetched data
 * URLs (e.g. if Vite-bundled asset paths ever stop resolving cleanly inside
 * `@react-pdf/renderer`). Default behavior is to use the imported asset paths
 * directly — react-pdf treats them as standard image sources.
 */
interface AppendixSectionProps {
  stationFrontDataUrl?: string;
  stationSideDataUrl?: string;
}

const styles = StyleSheet.create({
  root: {
    marginTop: palette.spacing.xl,
    paddingTop: palette.spacing.lg,
    borderTopWidth: 0.5,
    borderTopStyle: "solid",
    borderTopColor: palette.border,
    flexDirection: "column",
  },
  h1: {
    fontSize: 24,
    fontFamily: "Helvetica-Bold",
    color: palette.text,
    marginBottom: palette.spacing.lg,
  },
  h2: {
    fontSize: palette.type.h2,
    fontFamily: "Helvetica-Bold",
    color: palette.text,
    marginBottom: palette.spacing.md,
  },
  bulletRow: {
    flexDirection: "row",
    marginBottom: palette.spacing.sm,
  },
  bulletMarker: {
    width: 12,
    fontSize: palette.type.body,
    color: palette.muted,
  },
  bulletText: {
    flex: 1,
    fontSize: palette.type.body,
    color: palette.text,
    lineHeight: 1.5,
  },
  bold: {
    fontFamily: "Helvetica-Bold",
  },
  photoRow: {
    flexDirection: "row",
    marginTop: palette.spacing.lg,
    marginHorizontal: -palette.spacing.sm,
  },
  photoCell: {
    width: "50%",
    paddingHorizontal: palette.spacing.sm,
  },
  photoFrame: {
    width: "100%",
    aspectRatio: 16 / 9,
    backgroundColor: palette.softBg,
    borderRadius: 6,
    overflow: "hidden",
  },
  photoImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
});

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <View style={styles.bulletRow}>
      <Text style={styles.bulletMarker}>·</Text>
      <Text style={styles.bulletText}>{children}</Text>
    </View>
  );
}

export function AppendixSection({
  stationFrontDataUrl,
  stationSideDataUrl,
}: AppendixSectionProps = {}) {
  const frontSrc = stationFrontDataUrl ?? stationFront;
  const sideSrc = stationSideDataUrl ?? stationSide;

  return (
    <View style={styles.root} break>
      <Text style={styles.h1}>Appendix</Text>

      <Text style={styles.h2}>Station information</Text>

      <Bullet>
        All stations when installed are covered with a protective band. The
        back of the station is completely smooth (no rough or sharp edges) and
        the attachment ends are surrounded by rubber and fabric for protection.
      </Bullet>
      <Bullet>A station is approximately 25 kg (55 pounds).</Bullet>
      <Bullet>
        No station will be installed facing a court. It will always be facing
        away from the court and on the opposite side of the fence than the
        court. Thus, there is no possibility for someone playing sport to run
        into the station.
      </Bullet>
      <Bullet>
        Stations are mounted &amp; tied to the posts, so no damage or changes
        are ever made to the poles (no hole drilling, etc.).
      </Bullet>
      <Bullet>
        <Text style={styles.bold}>Address: </Text>Most accurate address found
        during Digital Mapping.
      </Bullet>
      <Bullet>
        <Text style={styles.bold}>GPS: </Text>Exact location of the desired
        pole for station installation.
      </Bullet>

      <View style={styles.photoRow}>
        <View style={styles.photoCell}>
          <View style={styles.photoFrame}>
            <Image src={frontSrc} style={styles.photoImage} />
          </View>
        </View>
        <View style={styles.photoCell}>
          <View style={styles.photoFrame}>
            <Image src={sideSrc} style={styles.photoImage} />
          </View>
        </View>
      </View>
    </View>
  );
}
